import DownloadIcon from "@mui/icons-material/Download"
import ElectricBoltIcon from "@mui/icons-material/ElectricBolt"
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered"
import HistoryIcon from "@mui/icons-material/History"
import HouseIcon from "@mui/icons-material/House"
import InfoIcon from "@mui/icons-material/Info"
import RouteIcon from "@mui/icons-material/Route"
import SendIcon from "@mui/icons-material/Send"
import SummarizeIcon from "@mui/icons-material/Summarize"
import { Box, Stack, Typography } from "@mui/material"
import Grid from "@mui/material/Unstable_Grid2"
import { ReactElement, useState } from "react"
import { useParams } from "react-router-dom"

import { ApplicationCommentsCard } from "../../components/comments/applicationComments/applicationCommentCard"
import {
    AppAccordion,
    AppAccordionDetails,
    AppAccordionSummary,
} from "../../components/dataDisplay/accordions/appAccordions"
import AppChip from "../../components/dataDisplay/chips/appChip"
import StackedKeyValueList from "../../components/dataDisplay/stackedKeyValueList"
import DataLoadError from "../../components/fallback/dataLoadError"
import { ConnectionsIcon } from "../../components/icons/icons"
import UserSuggestionAutocomplete from "../../components/inputs/controlled/userSuggestionAutocomplete"
import ApplicationTimeline from "../../components/installerInterface/applicationTimeline"
import KpiCircleIcon from "../../components/kpiCards/kpiCircleIcon"
import SingleApplicationSkeleton from "../../components/loading/skeletons/SingleApplicationSkeleton"
import LctChip from "../../components/pills/lct/lctChip"
import TagChip from "../../components/pills/tag/tagChip"
import DnoCommissionDecisionForm from "../../components/popUps/dnoResponse/dnoCommissionDecisionForm"
import DnoResponseForm from "../../components/popUps/dnoResponse/dnoResponseForm"
import AmendmentHistoryCard from "../../components/singleApplication/amendmentHistory/amendmentHistoryCard"
import Attachments from "../../components/singleApplication/attachments/dnoAttachments"
import DetailListAccordion from "../../components/singleApplication/detailAccordions/detailListAccordion"
import NestedDetailListAccordion from "../../components/singleApplication/detailAccordions/nestedDetailListAccordion"
import DuplicateApplications from "../../components/singleApplication/duplicateApplications/duplicateApplications"
import EnaAssessment from "../../components/singleApplication/enaAssessment/enaAssessment"
import ExistingDevices from "../../components/singleApplication/existingDevices/existingDevices"
import ExportMpanCard from "../../components/singleApplication/exportMpan/exportMpanCard"
import DnoApplicationProgressStepper from "../../components/singleApplication/progressStepper/dnoApplicationProgressStepper"
import ProposedDevices from "../../components/singleApplication/proposedDevices/proposedDevices"
import { formatKeys } from "../../components/singleApplication/singleApplicationKeys"
import SupplyDetails from "../../components/singleApplication/supplyDetails/supplyDetails"
import {
    AppCard,
    AppCardContent,
    AppCardHeader,
} from "../../components/surfaces/appCard"
import DisplayIf from "../../components/wrappers/displayIf"
import {
    ApplicationClasses,
    ApplicationStatuses,
    ApplicationDisplayStatuses,
    DnoEscalationStatuses,
} from "../../constants/enums"
import { NA } from "../../constants/generalConstants"
import { DNO_REVIEWER } from "../../constants/roles"
import {
    TT_EXISTING_DEVICES,
    TT_TAD,
    TT_TOTAL_INSTALLED_GEN,
    TT_TOT_DEVICES,
} from "../../constants/tooltips"
import useDownloadJson from "../../hooks/useDownloadJson"
import { useGetUserData } from "../../hooks/useGetUserData"
import { useScreenSize } from "../../hooks/useScreenSize"
import useDnoSingleApplication from "../../services/middleware/hooks/dnoInterface/protected/useDnoSingleApplication"
import { User } from "../../services/middleware/hooks/dnoInterface/protected/useGetAssigneeById"
import usePostApplicationAssignment from "../../services/middleware/hooks/dnoInterface/protected/usePostApplicationAssignment"
import {
    TCommissioningDeviceData,
    TDisplayActionHistory,
} from "../../services/middleware/types/installerInterface/installerApplicationTypes"
import { GRID_SPACING } from "../../themes/defaultThemeParams"
import {
    arrayOnCondition,
    formatDateTime,
    removeUndefinedAndNulls,
} from "../../utils/general/generalUtils"
import { applicationHasGenerationDevice } from "../../utils/submitApplication/submitApplicationUtils"
import HeaderBanner from "../lowCarbonTechDatabases/headerBanner"

const COLUMNS = 12

interface IDnoSingleApplicationParams {
    applicationId: string
}

/**
 * Single application summary page for DNOs. Note the attachments component
 * is displayed in a different part of the screen for large and extra large
 * screens vs medium and touch screen devices.
 */
const DnoSingleApplication = (): ReactElement => {
    const { applicationId } = useParams<
        keyof IDnoSingleApplicationParams
    >() as IDnoSingleApplicationParams
    const {
        application,
        isLoading,
        isValidating,
        mutate,
        isEscalation,
        downloadData,
    } = useDnoSingleApplication(applicationId)
    const { downloadJson } = useDownloadJson()
    const { isTabletOrMob } = useScreenSize()
    const { groups } = useGetUserData()
    const [isResponseOpen, setIsResponseOpen] = useState(false)
    const [isCommissionDecisionOpen, setIsCommissionDecisionOpen] =
        useState(false)

    const { postUsersId } = usePostApplicationAssignment(applicationId)

    const assignUser = (
        newValue: User | null,
        resetOnFail: () => void
    ): void => {
        postUsersId(newValue?.username || "", resetOnFail)
    }

    // Check if user has DNO_REVIEWER role
    const isDnoReviewer = groups.includes(DNO_REVIEWER)

    const isEligibleForCxReview =
        application?.applicationStatus === DnoEscalationStatuses.cxSubmitted &&
        (application.applicationClass === ApplicationClasses.genDemandGeneral ||
            application.applicationClass === ApplicationClasses.genGeneral)

    if (isLoading) {
        return <SingleApplicationSkeleton />
    }

    if (!application) {
        return <DataLoadError />
    }

    const steps = application.actionHistoryToDisplay
        ? application.actionHistoryToDisplay
              .filter((step) => step.displayToDno)
              .map((step: TDisplayActionHistory) => ({
                  // API returns the old status string; translate at render time
                  labelBold: step.title.replace(
                      ApplicationStatuses.dnoFurtherInfoRequired,
                      ApplicationDisplayStatuses.installerInformationRequired
                  ),
                  labelFeint: formatDateTime(step.time),
                  description: (
                      <StackedKeyValueList
                          details={removeUndefinedAndNulls(
                              Object.fromEntries(
                                  Object.entries(step.details).map(([k, v]) => [
                                      k,
                                      v ===
                                      ApplicationStatuses.dnoFurtherInfoRequired
                                          ? ApplicationDisplayStatuses.installerInformationRequired
                                          : v,
                                  ])
                              )
                          )}
                      />
                  ),
              }))
        : []

    return (
        <>
            <DnoResponseForm
                applicationId={applicationId}
                applicationDate={application.createdDate}
                isOpen={isResponseOpen}
                setIsOpen={setIsResponseOpen}
                mutate={mutate}
                applicationClass={application.applicationClass}
                applicationAddress={application.address}
                applicationStatus={application.applicationStatus}
            />
            <DnoCommissionDecisionForm
                applicationId={applicationId}
                applicationAddress={application.address}
                setIsOpen={setIsCommissionDecisionOpen}
                isOpen={isCommissionDecisionOpen}
                applicationPostcode={application.summaryFacts["Post Code"]}
                applicationMpan={application.customerInstallerDetails.mpan}
                applicationDno={application.dno}
                mutate={mutate}
            />
            <Stack spacing={GRID_SPACING}>
                <HeaderBanner
                    title="Application Summary"
                    subtitle={application.address}
                    subSubtitle={`${applicationId} \u2022 ${
                        application.applicationStatus ===
                        ApplicationStatuses.dnoFurtherInfoRequired
                            ? ApplicationDisplayStatuses.installerInformationRequired
                            : application.applicationStatus
                    }`}
                    reSubmitted={application.isResubmit}
                    actions={[
                        ...arrayOnCondition(!!isEscalation, [
                            {
                                text: "Make Decision",
                                color: "primary" as const,
                                endIcon: <SendIcon />,
                                onClick: () => setIsResponseOpen(true),
                            },
                        ]),
                        ...arrayOnCondition(isEligibleForCxReview, [
                            {
                                text: "Commissioning Decision",
                                color: "primary" as const,
                                endIcon: <SendIcon />,
                                onClick: () =>
                                    setIsCommissionDecisionOpen(true),
                            },
                        ]),
                        {
                            text: "Download",
                            color: "primary" as const,
                            endIcon: <DownloadIcon />,
                            onClick: () =>
                                application &&
                                downloadJson(
                                    {
                                        results: [downloadData],
                                    },
                                    "singleApplication"
                                ),
                        },
                    ]}
                    redacted={application.isRedacted}
                />
                <Grid
                    container
                    spacing={GRID_SPACING}
                    sx={{ flexWrap: { lg: "nowrap" } }}
                >
                    <Grid lg={3} xs={COLUMNS}>
                        <Stack spacing={GRID_SPACING}>
                            <AppCard>
                                <AppCardHeader
                                    avatar={<SummarizeIcon fontSize="large" />}
                                    title="Summary"
                                    subheader="Quick Facts"
                                    titleTypographyProps={{
                                        variant: "h5",
                                        fontWeight: "500",
                                    }}
                                />
                                <AppCardContent sx={{ overflow: "none" }}>
                                    <Stack spacing={1}>
                                        <Stack
                                            spacing={1}
                                            direction="row"
                                            useFlexGap
                                            flexWrap="wrap"
                                        >
                                            {application.tag && (
                                                <TagChip
                                                    tag={application.tag}
                                                />
                                            )}
                                            <AppChip
                                                sx={{ mr: 1 }}
                                                label={
                                                    application.applicationStatus
                                                }
                                                variant="outlined"
                                                color="primary"
                                            />
                                            {application.lcts.map((lct) => (
                                                <LctChip
                                                    key={lct}
                                                    lct={lct}
                                                    sx={{ mr: 1 }}
                                                />
                                            ))}
                                        </Stack>
                                        <StackedKeyValueList
                                            details={application.summaryFacts}
                                            spacing={1}
                                        />
                                        <Stack>
                                            <Typography
                                                color="text.secondary"
                                                variant="body2"
                                            >
                                                Assigned to
                                            </Typography>
                                            <UserSuggestionAutocomplete
                                                onSelectedUserChange={
                                                    assignUser
                                                }
                                                userId={application.assignedTo}
                                                sx={{ marginTop: -1.5 }}
                                            />
                                        </Stack>
                                    </Stack>
                                </AppCardContent>
                            </AppCard>
                            <KpiCircleIcon
                                title="Number of Devices"
                                value={String(
                                    application.devicesToInstall.length
                                )}
                                icon={FormatListNumberedIcon}
                                tooltip={TT_TOT_DEVICES}
                            />
                            {application?.totAggGen &&
                            application?.totAggGen !== NA ? (
                                <KpiCircleIcon
                                    title="Total Installed Generation"
                                    value={`${application.totAggGen} kW`}
                                    color="secondary"
                                    icon={ConnectionsIcon}
                                    tooltip={TT_TOTAL_INSTALLED_GEN}
                                />
                            ) : (
                                <KpiCircleIcon
                                    title="Existing Devices on Site"
                                    value={String(
                                        application.existingDevices.length
                                    )}
                                    icon={HouseIcon}
                                    color="secondary"
                                    tooltip={TT_EXISTING_DEVICES}
                                />
                            )}
                            <KpiCircleIcon
                                title="Total Demand"
                                value={`${application.maxDemand} Amps`}
                                icon={ConnectionsIcon}
                                tooltip={TT_TAD}
                            />
                            <Stack
                                spacing={GRID_SPACING}
                                sx={{
                                    display: { xs: "none", lg: "flex" },
                                }}
                            >
                                <Attachments
                                    applicationId={applicationId}
                                    attachments={application.cutOutImages}
                                    additionalAttachments={
                                        application.additionalAttachments
                                    }
                                    isDnoMode
                                    disabled={application.isRedacted}
                                />
                                {application.commissioningInfo &&
                                    application.commissioningInfo.files
                                        .length !== 0 && (
                                        <Attachments
                                            title="Commissioning Attachments"
                                            applicationId={applicationId}
                                            attachments={
                                                application.commissioningInfo
                                                    .files
                                            }
                                            additionalAttachments={[]}
                                            isDnoMode
                                            disabled={application.isRedacted}
                                        />
                                    )}
                            </Stack>
                        </Stack>
                    </Grid>
                    <Grid lg={9} xs={COLUMNS}>
                        <Stack spacing={GRID_SPACING}>
                            {!isTabletOrMob && (
                                <AppCard>
                                    <AppCardHeader
                                        avatar={<RouteIcon fontSize="large" />}
                                        title="Application Progress"
                                        subheader="Journey of the application through Connect Direct"
                                        titleTypographyProps={{
                                            variant: "h5",
                                            fontWeight: "500",
                                        }}
                                    />
                                    <AppCardContent>
                                        <DnoApplicationProgressStepper
                                            status={
                                                application.applicationStatus
                                            }
                                            dno={application.dno}
                                            isGeneration={applicationHasGenerationDevice(
                                                application
                                            )}
                                        />
                                    </AppCardContent>
                                </AppCard>
                            )}
                            <Grid
                                container
                                spacing={GRID_SPACING}
                                sx={{ flexWrap: { lg: "nowrap" } }}
                            >
                                <Grid lg={6} xs={COLUMNS}>
                                    <Stack spacing={GRID_SPACING}>
                                        <EnaAssessment
                                            enaAssessment={
                                                application.enaAssessment
                                            }
                                        />
                                        <AppCard>
                                            <AppCardHeader
                                                avatar={
                                                    <ElectricBoltIcon fontSize="large" />
                                                }
                                                title={`Proposed Device${
                                                    application.existingDevices
                                                        .length > 1
                                                        ? "s"
                                                        : ""
                                                }`}
                                                subheader="Outlines the equipment the customer wishes to install"
                                                titleTypographyProps={{
                                                    variant: "h5",
                                                    fontWeight: "500",
                                                }}
                                            />
                                            <AppCardContent>
                                                <ProposedDevices
                                                    proposedDevices={
                                                        application.devicesToInstall
                                                    }
                                                />
                                            </AppCardContent>
                                        </AppCard>
                                    </Stack>
                                </Grid>
                                <Grid lg={6} xs={COLUMNS}>
                                    <Stack spacing={GRID_SPACING}>
                                        <DisplayIf
                                            condition={
                                                !!application.actionHistoryToDisplay
                                            }
                                        >
                                            <AppCard>
                                                <AppCardHeader
                                                    avatar={
                                                        <HistoryIcon fontSize="large" />
                                                    }
                                                    title="Application History"
                                                    subheader="An of key past actions taken on this application"
                                                    titleTypographyProps={{
                                                        variant: "h5",
                                                        fontWeight: "500",
                                                    }}
                                                />
                                                <ApplicationTimeline
                                                    applicationSteps={steps}
                                                />
                                            </AppCard>
                                        </DisplayIf>
                                        <AppCard>
                                            <AppCardHeader
                                                avatar={
                                                    <InfoIcon fontSize="large" />
                                                }
                                                title="Other Details"
                                                subheader="Click into the boxes to see further information"
                                                titleTypographyProps={{
                                                    variant: "h5",
                                                    fontWeight: "500",
                                                }}
                                            />
                                            <AppCardContent>
                                                <Stack spacing={GRID_SPACING}>
                                                    <DetailListAccordion
                                                        title="Customer and Installer Details"
                                                        kwargs={formatKeys(
                                                            application.customerInstallerDetails
                                                        )}
                                                    />
                                                    <AppAccordion>
                                                        <AppAccordionSummary>
                                                            <Typography fontWeight="500">
                                                                Supply Details
                                                            </Typography>
                                                        </AppAccordionSummary>
                                                        <AppAccordionDetails>
                                                            <SupplyDetails
                                                                applicationId={
                                                                    applicationId
                                                                }
                                                                supplyDetails={
                                                                    application.supplyDetails
                                                                }
                                                                disabled={
                                                                    application.isRedacted
                                                                }
                                                            />
                                                        </AppAccordionDetails>
                                                    </AppAccordion>
                                                    <AppAccordion>
                                                        <AppAccordionSummary>
                                                            <Typography fontWeight="500">
                                                                {`Existing Devices (${application.existingDevices.length})`}
                                                            </Typography>
                                                        </AppAccordionSummary>
                                                        <AppAccordionDetails>
                                                            <ExistingDevices
                                                                existingDevices={
                                                                    application.existingDevices
                                                                }
                                                            />
                                                        </AppAccordionDetails>
                                                    </AppAccordion>

                                                    {application.duplicateApplicationIds && (
                                                        <AppAccordion>
                                                            <AppAccordionSummary>
                                                                <Typography fontWeight="500">
                                                                    {`Duplicate Applications (${
                                                                        application
                                                                            .duplicateApplicationIds
                                                                            .length
                                                                    })`}
                                                                </Typography>
                                                            </AppAccordionSummary>
                                                            <AppAccordionDetails>
                                                                <DuplicateApplications
                                                                    duplicateApplications={
                                                                        application.duplicateApplicationIds
                                                                    }
                                                                />
                                                            </AppAccordionDetails>
                                                        </AppAccordion>
                                                    )}

                                                    {application.commissioningInfo && (
                                                        <NestedDetailListAccordion
                                                            title="Commissioning Details"
                                                            kwargs={{
                                                                "Site Information":
                                                                    formatKeys(
                                                                        application
                                                                            .commissioningInfo
                                                                            .siteData
                                                                    ),
                                                                ...application.commissioningInfo.devices?.reduce<
                                                                    Record<
                                                                        string,
                                                                        TCommissioningDeviceData
                                                                    >
                                                                >(
                                                                    (
                                                                        acc,
                                                                        deviceInfo
                                                                    ) => ({
                                                                        ...acc,
                                                                        [deviceInfo.deviceSysRef]:
                                                                            formatKeys(
                                                                                deviceInfo
                                                                            ),
                                                                    }),
                                                                    {}
                                                                ),
                                                            }}
                                                        />
                                                    )}
                                                </Stack>
                                            </AppCardContent>
                                        </AppCard>
                                        <AmendmentHistoryCard
                                            amendmentHistory={
                                                application.amendmentHistory ||
                                                []
                                            }
                                            applicationId={applicationId}
                                            isRedacted={application.isRedacted}
                                        />
                                        <ApplicationCommentsCard
                                            title="Comments"
                                            subheader="Use this thread to communicate with the DNO reviewing your application. Please note, comments posted here cannot be deleted and are supplementary to your application"
                                            applicationId={applicationId}
                                            mutate={mutate}
                                            fetchingComments={
                                                isLoading || isValidating
                                            }
                                            comments={
                                                application.comments ?? []
                                            }
                                            isRedacted={application.isRedacted}
                                            isDno
                                            commentStatus={
                                                application.isDNOThreadUnread
                                            }
                                            lastReadBy={
                                                application.lastReadByDnoUsername
                                            }
                                            lastReadAt={
                                                application.lastReadByDnoTimestamp
                                            }
                                        />
                                        <DisplayIf
                                            condition={
                                                application.applicationStatus ===
                                                    ApplicationStatuses.cxAccepted &&
                                                isDnoReviewer
                                            }
                                        >
                                            <ExportMpanCard
                                                applicationId={applicationId}
                                                applicationMpan={String(
                                                    application
                                                        .customerInstallerDetails
                                                        .mpan
                                                )}
                                                mutate={mutate}
                                            />
                                        </DisplayIf>
                                    </Stack>
                                </Grid>
                            </Grid>
                            <Box
                                sx={{
                                    display: { xs: "block", lg: "none" },
                                }}
                            >
                                <Stack spacing={GRID_SPACING}>
                                    <Attachments
                                        applicationId={applicationId}
                                        attachments={application.cutOutImages}
                                        additionalAttachments={
                                            application.additionalAttachments
                                        }
                                        isDnoMode
                                    />
                                    {application.commissioningInfo &&
                                        application.commissioningInfo.files
                                            .length !== 0 && (
                                            <Attachments
                                                title="Commissioning Attachments"
                                                applicationId={applicationId}
                                                attachments={
                                                    application
                                                        .commissioningInfo.files
                                                }
                                                additionalAttachments={[]}
                                                isDnoMode
                                            />
                                        )}
                                </Stack>
                            </Box>
                        </Stack>
                    </Grid>
                </Grid>
            </Stack>
        </>
    )
}

export default DnoSingleApplication
